import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { photoUrl } from "../../Variables";
import { amount, area, categoryLabel, placeLabel } from "./labels";
import { explainMatch } from "./explainMatch";

const BADGE = { strong: "bg-green-700 text-white", close: "bg-blue-100 text-blue-900", alternative: "bg-slate-200 text-slate-800",
  excellent: "bg-green-700 text-white", good: "bg-blue-100 text-blue-900", partial: "bg-slate-200 text-slate-800" };
const LEVEL_KEY = { strong: "Strong", close: "Close", alternative: "Alternative" };

function Photo({ listing }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  if (!listing.photo || failed)
    return <div role="img" aria-label={t("propertyRequest.matches.noPhoto")} className="flex h-full w-full items-center justify-center bg-slate-100 text-xs text-slate-500">{t("propertyRequest.matches.noPhoto")}</div>;
  return <img src={photoUrl(listing.photo, listing.folder)} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />;
}

// A real listing returned by the API (public fields only); the link opens its existing page.
// Matching levels (strong/close/alternative) or proposal levels (excellent/good/partial); children add proposal details.
export function MatchCard({ match, children = null, aside = null }) {
  const { t, i18n } = useTranslation();
  const { listing, level } = match;
  const transaction = listing.kind === "vacation" ? "vacation" : listing.transaction;
  const facts = [
    categoryLabel(t, listing.category, transaction),
    placeLabel(t, listing.city, listing.delegation ? [listing.delegation] : []),
    listing.surface ? area(listing.surface) : null,
    listing.bedrooms ? t("propertyRequest.matches.bedrooms", { count: listing.bedrooms }) : null,
  ].filter(Boolean);
  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" data-match-level={level} data-listing-id={listing.id}>
      <div className="flex gap-3 p-3 sm:gap-4 sm:p-4">
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl sm:h-32 sm:w-32"><Photo listing={listing} /></div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${BADGE[level]}`}>
              {LEVEL_KEY[level] ? t(`propertyRequest.matches.badge${LEVEL_KEY[level]}`) : t(`propertyRequest.level.${level}`)}
            </span>
            {aside}
          </div>
          <h3 className="break-words text-base font-bold text-slate-900 sm:text-lg">{listing.title || categoryLabel(t, listing.category, transaction)}</h3>
          <p className="break-words text-sm text-slate-600">{facts.join(" · ")}</p>
          {listing.price != null && <p className="text-lg font-extrabold text-green-800"><bdi>{amount(t, listing.transaction)(listing.price)}</bdi></p>}
        </div>
      </div>
      <div className="mx-3 mb-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-800 sm:mx-4">
        <h4 className="font-bold">{t("propertyRequest.matches.why")}</h4>
        <p>{explainMatch(t, match, i18n.language)}</p>
      </div>
      {children}
      <div className="border-t border-slate-100 px-4 py-3">
        <Link to={listing.url} className="font-semibold text-green-800 underline">{t("propertyRequest.matches.view")}</Link>
      </div>
    </article>
  );
}

// Very good matches, good matches and alternatives; nothing below the alternative threshold is returned.
export default function PropertyRequestMatches({ result, onPublish, onEdit }) {
  const { t } = useTranslation();
  const sections = [["strong", result.strong], ["close", result.close], ["alternative", result.alternative]].filter(([, items]) => items?.length);
  return (
    <div className="space-y-6">
      {sections.length === 0 ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm" data-no-match>
          <p className="text-lg font-bold text-slate-900">{t("propertyRequest.matches.none")}</p>
          <p className="mt-1 text-slate-600">{t("propertyRequest.matches.publishQuestion")}</p>
        </section>
      ) : sections.map(([level, items]) => (
        <section key={level} aria-labelledby={`pr-matches-${level}`} className="space-y-3">
          <h2 id={`pr-matches-${level}`} className="text-xl font-bold text-slate-900">{t(`propertyRequest.matches.${level}`)} <span className="text-base font-normal text-slate-500">({items.length})</span></h2>
          {items.map((match) => <MatchCard key={`${match.listing.kind}:${match.listing.id}`} match={match} />)}
        </section>
      ))}
      <div className="space-y-2 rounded-3xl bg-green-50 p-4">
        {sections.length > 0 && <p className="text-sm text-slate-700">{t("propertyRequest.matches.noneFit")}</p>}
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" className="lina-action-primary h-12 text-base" onClick={onPublish}>{t("propertyRequest.matches.publish")}</button>
          <button type="button" className="lina-action h-12 text-base" onClick={onEdit}>{t("propertyRequest.matches.edit")}</button>
        </div>
      </div>
    </div>
  );
}
